/** Puts the screen back together after the iOS keyboard has gone away.
 *
 *  While the keyboard is up, iOS shrinks the visible part of the page and slides
 *  it down inside the layout one to keep the field in view: that gap is
 *  `visualViewport.offsetTop`, and it is the keyboard's height. When the
 *  keyboard closes it is meant to go back to zero. Sometimes it does not —
 *  closed with Done, with Enter, or by tapping a lead while the search still had
 *  it — and everything pinned to the screen stays pinned to where the screen
 *  used to be. The app bar sits a keyboard's height above the top edge, out of
 *  sight, and the action bar floats a keyboard's height above the bottom one,
 *  over the middle of the drawing. The first real scroll puts it right, which is
 *  no comfort to somebody who has not scrolled yet.
 *
 *  So this does the scroll for them: once the keyboard has settled, if the gap
 *  is still there, one pixel and straight back, in two frames so that iOS sees
 *  a scroll rather than two requests that cancel out. Only after a field had
 *  focus, and only once per field, so a phone where the nudge does not help
 *  cannot end up nudging forever. Never while zoomed in: pinched, the gap is
 *  where the person is looking, not a mistake. */
export function settleAfterKeyboard(): void {
  const view = window.visualViewport;
  if (!view) return;

  let armed = false;
  let timer = 0;

  document.addEventListener("focusin", () => {
    if (typing()) armed = true;
  });

  const settle = () => {
    if (!armed) return;
    window.clearTimeout(timer);
    // Past the end of the keyboard's slide, so the gap measured is the one it
    // left behind rather than one it is still closing.
    timer = window.setTimeout(() => {
      if (typing()) return;
      armed = false;
      if (view.offsetTop < 1 || Math.abs(view.scale - 1) > 0.01) return;
      const y = window.scrollY;
      window.scrollTo(0, y > 0 ? y - 1 : y + 1);
      requestAnimationFrame(() => window.scrollTo(0, y));
    }, 300);
  };

  document.addEventListener("focusout", settle);
  view.addEventListener("resize", settle);
}

/** Whether what has focus is something the keyboard is up for. A checkbox or a
 *  button can hold focus too, and neither brings one up. */
function typing(): boolean {
  const el = document.activeElement;
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
  if (el instanceof HTMLInputElement) {
    return !["checkbox", "radio", "button", "submit", "reset", "range", "color", "file", "image"].includes(el.type);
  }
  return el instanceof HTMLElement && el.isContentEditable;
}
